import { Observable } from 'rxjs/Observable'
import { Epic, ActionsObservable } from 'redux-observable'
import { RootState } from '../types'
import { Action, MiddlewareAPI } from 'redux'
import { I18n } from 'react-redux-i18n'
import {
    CLOSE_TRADE,
    FULFILL_CANCEL_PENDING_ORDER,
    FULFILL_CLOSE_TRADE,
    FULFILL_LOGIN,
    FULFILL_MODIFY_PENDING_TRADE,
    FULFILL_MODIFY_TRADE,
    FULFILL_OPEN_TRADE,
    FULFILL_PENDING_TRADE,
    MODIFY_TRADE,
    OPEN_TRADE,
    SET_TRADING_SYMBOL,
    SET_TRADING_SIGNAL,
    TRIGGER_POST_COMMAND,
    INPUT_POSITION_PRICE,
    SET_PRICE,
    SELECT_POSITION,
    SELECT_ORDER,
    FULFILL_CURRENT_MARKET_STATE,
} from '../constants'
import {
  applyPostCommand,
  disableTradeControls,
  enableTradeControls,
  SetTradingSymbol,
  SetTradingSignal,
  TriggerPostCommand,
  setTradingSignal,
  setTradingSymbol,
} from '../actions/commonActions'
import {
  setTradeType,
  InputPositionPrice,
  inputPositionSL,
  inputPositionTP,
  SetPrice,
  setStopLoss,
  setTakeProfit,
} from '../actions/TradeActions'
import {
  changeSymbol,
  errorObservable,
  selectPosition,
  selectOrder,
  setType,
  toggleTradingPane,
} from '../actions'
import { MT4Connector } from '@bdswiss/mt4-connector'

const mt4 = MT4Connector.Instance

export const disableTradeControlsEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(OPEN_TRADE, MODIFY_TRADE, CLOSE_TRADE).map((action: any) => {
    return disableTradeControls(action.id)
  })

export const enableTradeControlsEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$
    .ofType(
      FULFILL_OPEN_TRADE,
      FULFILL_PENDING_TRADE,
      FULFILL_MODIFY_TRADE,
      FULFILL_CLOSE_TRADE,
      FULFILL_MODIFY_PENDING_TRADE,
      FULFILL_CANCEL_PENDING_ORDER
    )
    .map((action: any) => {
      return enableTradeControls(action.id)
    })

export const enableTradeControlsOnLoginEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(FULFILL_LOGIN).switchMap(action => {
    const { appState } = state.getState()
    const asset = mt4.assets[appState.symbol]
    const currentLogin = appState.activeAccount
    const accountTypename = state
      .getState()
      .tradingAccounts.viewer.accounts.find(acc => acc.login === currentLogin)!.__typename
    return Observable.of<Action>(
      setType(accountTypename),
      asset.marketState === 0 ? enableTradeControls() : disableTradeControls()
    )
  })

export const setTradingSymbolEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<SetTradingSymbol>).ofType(SET_TRADING_SYMBOL).switchMap(action => {
    const { symbol, tradeType } = action
    const actions: Action[] = []
    const { signal: currentSignal } = state.getState().appState
    if (!mt4.assets[symbol]) {
      return errorObservable(new Error(I18n.t('invalidSymbol', symbol)))
    }
    // if (state.getState().appState.symbol !== symbol) {
    //   actions.push(changeSymbol(symbol))
    // }
    if (currentSignal == null) {
      if (state.getState().tradingPane.tradeType !== tradeType) {
        actions.push(setTradeType(tradeType))
      }
    }

    if (mt4.assets[symbol].marketState !== 0) {
      actions.push(disableTradeControls())
    } else {
      actions.push(enableTradeControls())
    }

    if (!state.getState().appState.tradingPaneOpened) {
      actions.push(toggleTradingPane())
    }

    return actions.length ? Observable.from(actions) : Observable.empty()
  })

export const setTradingSignalEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<SetTradingSignal>).ofType(SET_TRADING_SIGNAL).switchMap(action => {
    const {
      signal,
      signal: { symbol },
    } = action
    const { signal: currentSignal, symbol: stateSymbol } = state.getState().appState
    const { tradeType: stateTradeType } = state.getState().tradingPane
    const actions: Action[] = []
    const tradeableAsset = mt4.assets[symbol]
    if (!tradeableAsset) {
      return errorObservable(new Error(I18n.t('invalidSymbol', symbol)))
    } else {
      if (currentSignal !== signal) {
        actions.push(setTradingSignal(signal))
      }
      const tradeType = signal.direction === 'UP' ? 'buy' : 'sell'
      if (stateTradeType !== tradeType || stateSymbol !== tradeableAsset.symbol) {
        actions.push(setTradingSymbol(tradeableAsset.symbol, tradeType))
      }
    }
    return actions.length ? Observable.from(actions) : Observable.empty()
  })

export const triggerPostCommandEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<TriggerPostCommand>)
    .ofType(TRIGGER_POST_COMMAND)
    .mergeMap(action => {
      const { symbol, tradeType, timeScale, stopLoss, takeProfit } = action
      if (!mt4.assets[symbol]) {
        return errorObservable(new Error(I18n.t('invalidSymbol', symbol)))
      }
      if (state.getState().appState.symbol !== symbol) {
        return Observable.of<Action>(
          changeSymbol(symbol),
          applyPostCommand(symbol, tradeType, timeScale, takeProfit, stopLoss)
        )
      } else {
        return Observable.of<Action>(
          applyPostCommand(symbol, tradeType, timeScale, takeProfit, stopLoss)
        )
      }
    })

export const resetSelectionOnClosePositionEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(FULFILL_CLOSE_TRADE).mergeMap((action: any) => {
    return state.getState().appState.selectedPosition === action.tradeObject.order
      ? Observable.of<Action>(selectPosition(NaN, false))
      : Observable.empty()
  })

export const resetSelectionOnCancelOrderEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(FULFILL_CANCEL_PENDING_ORDER).mergeMap((action: any) => {
    return state.getState().appState.selectedOrder === action.tradeObject.order
      ? Observable.of<Action>(selectOrder(NaN, false))
      : Observable.empty()
  })

export const resetPendingPositionSLTPEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<InputPositionPrice>)
    .ofType(INPUT_POSITION_PRICE)
    .flatMap(action => {
      const { order, price } = action
      const position = mt4.positions.get(order) // state.getState().pendingOrders[order];
      return position && position.openPrice !== price
        ? Observable.of<Action>(inputPositionSL(order, 0), inputPositionTP(order, 0))
        : Observable.empty()
    })

export const resetTradePanelSLTPEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<SetPrice>).ofType(SET_PRICE).flatMap(() => {
    return Observable.of<Action>(setStopLoss(0), setTakeProfit(0))
  })

export const selectPositionEpic: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(SELECT_POSITION, SELECT_ORDER).mergeMap((action: any) => {
    const { symbol: currentSymbol } = state.getState().appState
    const position = mt4.positions.get(action.order) || mt4.pendingOrders.get(action.order)
    const actions: Action[] = []
    if (position) {
      if (currentSymbol !== position.symbol) {
        actions.push(changeSymbol(position.symbol))
      }
    }
    return actions.length ? Observable.from(actions) : Observable.empty()
  })

export const triggerMarketStateChanges: Epic<Action, RootState> = (
    action$,
    state: MiddlewareAPI<RootState>
) =>
    action$.ofType(FULFILL_CURRENT_MARKET_STATE).map(action => {
        const { appState } = state.getState()
        const asset = mt4.assets[appState.symbol]
        return asset.marketState === 0 ? enableTradeControls() : disableTradeControls()

    })
